'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * Subscribe to a media-query match state. SSR-safe (returns `false` on the server) and uses
 * `useSyncExternalStore` so the render result is consistent across React's concurrent passes.
 *
 * @param query - CSS media query string (e.g., `(min-width: 768px)`).
 */
export function useMediaQuery(query: string): boolean {
  const mediaQueryList = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.matchMedia(query);
  }, [query]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!mediaQueryList) return () => {};
      const legacyMediaQueryList = mediaQueryList as MediaQueryList & {
        addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
        removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      };
      const listener = () => onStoreChange();
      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', listener);
        return () => mediaQueryList.removeEventListener('change', listener);
      }
      legacyMediaQueryList.addListener?.(listener);
      return () => legacyMediaQueryList.removeListener?.(listener);
    },
    [mediaQueryList],
  );

  const getSnapshot = useCallback(() => mediaQueryList?.matches ?? false, [mediaQueryList]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
